// gegenueberFolgen.ts — die Rollen mit Folgefeldern (`folge` in
// `kontaktRollen.ts`) als „Gegenüber"-Kästen im Status-Schritt (User
// 02.10.2026).
//
// Ihre Bezüge liegen NICHT in `kontaktStatusBezuege`, sondern in den Listen
// der Beziehungskarten (`rel_household`, `rel_weitere`, `rel_personFirma`,
// bei der Firma `vereine`). Daran ändert sich nichts — hier steht nur, welcher
// Kasten erscheint, wie seine Frage lautet und ob er noch offen ist.
//
// Rein und ohne Seiteneffekte — in der vitest-Umgebung „node" testbar.

import { rolleZu, type RollenFolge } from "./kontaktRollen";
import { STATUS_MENSCH_IM_UNTERNEHMEN } from "./sonstigeStatusKatalog";

/**
 * Ein Kasten je Liste: „person" (Haushalt und außerhalb in einer Liste),
 * „verbindung" (Unternehmen und Verein teilen sich `rel_personFirma`).
 */
export type FolgeKarte = "person" | "verbindung";

export const folgeKarten = (folgen: RollenFolge[]): FolgeKarte[] => [
  ...(folgen.includes("person") ? (["person"] as const) : []),
  ...(folgen.includes("unternehmen") || folgen.includes("verein") ? (["verbindung"] as const) : []),
];

const VEREIN_MITGLIED = "Mitgliedschaft in einem Verein";

const labelVon = (rolle: string): string => rolleZu(rolle)?.label ?? rolle;

const gross = (t: string): string => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

/**
 * Die Frage über dem Kasten, aus den gewählten Rollen — wie bei den
 * `bezug`-Rollen je Rolle formuliert („Verantwortung fürs Unternehmen — für
 * welches Unternehmen?"), nicht generisch.
 */
export const folgeFrage = (karte: FolgeKarte, rollen: string[]): string => {
  if (karte === "person") {
    const labels = rollen.filter((r) => rolleZu(r)?.folge === "person").map(labelVon);
    return `${gross(labels.length ? `Beziehung ${labels.join(" / ")}` : "Beziehung")} — zu welcher Person?`;
  }
  const hier = rollen.filter((r) => {
    const f = rolleZu(r)?.folge;
    return f === "unternehmen" || f === "verein";
  });
  const labels = hier.map(labelVon);
  const kopf = labels.length ? labels.join(" / ") : "Verbindung";
  const unternehmen = hier.some((r) => rolleZu(r)?.folge === "unternehmen");
  if (unternehmen) {
    const nurIm = hier.every((r) => r === STATUS_MENSCH_IM_UNTERNEHMEN);
    return `${kopf} — ${nurIm ? "in welchem" : "für welches"} Unternehmen?`;
  }
  const nurMitglied = hier.every((r) => r === VEREIN_MITGLIED);
  return `${kopf} — ${nurMitglied ? "in welchem" : "für welchen"} Verein?`;
};

/* ------------------------------------------------------------------ *
 * Offen oder erledigt
 * ------------------------------------------------------------------ */

type RefArtig = { contactId?: number | null; lastName?: string | null } | null | undefined;

/** Wie `refHasName`: bestehender Kontakt oder eingetippter Name. */
const hatGegenueber = (ref: RefArtig): boolean =>
  !!ref && (!!ref.contactId || !!(ref.lastName && String(ref.lastName).trim()));

const liste = (roh: string): any[] => {
  if (!roh || !roh.trim()) return [];
  try {
    const p = JSON.parse(roh);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};

/**
 * Fehlt dem Kasten noch sein Gegenüber? `lies` ist der Feld-Getter des
 * Formulars (`f`). Ein Eintrag ohne Namen zählt nicht — er würde beim
 * Speichern ohnehin verworfen. `firma`: Der Kontakt ist eine Firma.
 */
export const folgeKarteOffen = (
  karte: FolgeKarte,
  lies: (k: string) => string,
  firma = false,
): boolean => {
  if (karte === "person") {
    return ![...liste(lies("rel_household")), ...liste(lies("rel_weitere"))].some((it) =>
      hatGegenueber(it?.ref),
    );
  }
  const firmen = liste(lies("rel_personFirma")).some((it) => hatGegenueber(it?.ref));
  // Firmenanlage: die Vereine der Karte „Mitgliedschaften". Bei der Person
  // NICHT — dort schreibt der Kasten nach `rel_personFirma`, und die eigene
  // Mitgliedschaften-Karte der Person ist eine andere Angabe.
  const vereine = firma && liste(lies("vereine")).some(
    (v) => !!v?.vereinContactId || !!String(v?.vereinName ?? "").trim() || hatGegenueber(v?.ref),
  );
  return !(firmen || vereine);
};
